/**
 * Hook for fetching dynamic filter options from the backend.
 *
 * Provides cached filter options for payers, assessment types, stations, and assigned owners.
 * Shows empty lists by default until data is loaded from the API.
 */

import { useQuery, type QueryObserverResult } from "@tanstack/react-query";
import { apiService, type ApiService, type FilterOptionsResponse } from "../services/apiService";
import { transientErrorRefetchInterval } from "../utils/transientErrorRefetch";

// Empty default options - populated dynamically from API
const DEFAULT_FILTER_OPTIONS: FilterOptionsResponse = {
  statuses: [],
  payers: [],
  assessment_types: [],
  stations: [],
  buildings: [],
  assigned_owners: [],
};

interface FilterOption {
  value: string;
  label: string;
}

export interface FilterOptions {
  statuses: FilterOption[];
  payers: FilterOption[];
  assessmentTypes: FilterOption[];
  stations: FilterOption[];
  buildings: FilterOption[];
  assignedOwners: FilterOption[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<QueryObserverResult<FilterOptionsResponse, Error>>;
}

/**
 * Convert an UPPER_SNAKE_CASE or UPPER-KEBAB string to Title Case.
 * e.g. "DISCHARGE_RETURN_ANNOTATION" → "Discharge Return Annotation"
 */
function formatLabel(value: string): string {
  return value
    .replace(/[_-]/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Transform API response to filter option format with "All" option
 * (used by single-select filters — building, etc.)
 */
function toFilterOptions(values: string[], allLabel: string): FilterOption[] {
  const options: FilterOption[] = [{ value: "all", label: allLabel }];
  values.forEach((value) => {
    options.push({ value, label: formatLabel(value) });
  });
  return options;
}

/**
 * Transform API response to filter option format WITHOUT an "All" sentinel.
 * NETSOL-12004: multi-select filters (payer, assessment type) represent "all"
 * as an empty selection, so no sentinel option is needed.
 */
function toMultiSelectOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({ value, label: formatLabel(value) }));
}

/**
 * Hook to fetch and cache filter options from the backend
 * @param api - Optional API service instance. Defaults to the singleton apiService.
 * @param category - Optional work-item category (MDS/UDA). NETSOL-12075: scopes
 *   assessment_types to that category so each tab lists only its own types. The
 *   query key includes it so switching tabs refetches the right list.
 */
export function useFilterOptions(
  api: ApiService = apiService,
  category?: string,
): FilterOptions {
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useQuery<FilterOptionsResponse>({
    queryKey: ["filter-options", category ?? null],
    queryFn: () => api.getFilterOptions(category),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    // No retry: the bounded repeats ask again after a transient error, one request per
    // repeat (PR #740 review item 9); a 401 waits for the host's new token.
    retry: false,
    refetchOnWindowFocus: false,
    // LIIP-1036: after a 5xx or a network failure ask again (bounded, and on a tab return)
    // until the API is back; the dropdowns use the defaults (or the last answer) meanwhile.
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });

  // Use fetched data or fall back to defaults
  const filterData = data || DEFAULT_FILTER_OPTIONS;

  return {
    statuses: toFilterOptions(filterData.statuses, "Open Items"),
    payers: toMultiSelectOptions(filterData.payers),
    assessmentTypes: toMultiSelectOptions(filterData.assessment_types),
    stations: toFilterOptions(filterData.stations, "All Stations"),
    buildings: toFilterOptions(filterData.buildings, "All Buildings"),
    assignedOwners: [
      { value: "all", label: "All Owners" },
      { value: "Unassigned", label: "Unassigned" },
      ...filterData.assigned_owners
        .filter((name) => name && name !== "Unassigned")
        .map((name) => ({ value: name, label: name })),
    ],
    isLoading,
    isError,
    refetch,
  };
}
